import { Box, Typography, useTheme } from "@mui/material";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import AddButton from "../../../components/button/AddButton";
import RemoveButton from "../../../components/button/RemoveButton";
import AgGrid from "../../../components/grid/AgGrid";
import { defaultColDef, floatFormater, leftAlignColDef, rightAlignColDef } from "../../../components/grid/columnDefinitions/columnDefinitions";
import { getOrgTokens } from "../../../theme/style/organizationPalette";

/**
 * Editable, paginated AgGrid replacement for the stacked-card material rows —
 * each itemDef entry becomes a column instead of a repeated block of fields,
 * so long materials lists page instead of forcing the user to scroll a tall
 * stack of cards. Row identity is a synthetic __key (array index at render
 * time) since material rows are plain form data with no stable id of their
 * own — stripped back out before onChange ever sees the array.
 */
const MaterialCompositionGrid = ({ label, itemDef, items, onChange, editable = true, renameLabel = (s) => s }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const tok = getOrgTokens(theme.palette.mode === "dark");
  const [selectedKeys, setSelectedKeys] = useState([]);

  const rowData = useMemo(() => items.map((item, i) => ({ ...item, __key: i })), [items]);

  const columnDefs = useMemo(
    () =>
      itemDef.map((def) => {
        const base = {
          headerName: t(renameLabel(def.label)),
          field: def.field,
          ...(def.type === "float" || def.computed ? rightAlignColDef : leftAlignColDef),
          ...defaultColDef,
          minWidth: 140,
          flex: 1,
          // Override defaultColDef's wrapHeaderText/autoHeaderHeight — long
          // labels like "Material Composition Percentage" were wrapping to
          // 3 lines and making the header row unnecessarily tall.
          wrapHeaderText: false,
          autoHeaderHeight: false,
        };
        if (def.computed) {
          return {
            ...base,
            editable: false,
            valueGetter: (params) => {
              const totalQty = items.reduce((s, item) => s + (parseFloat(item.quantity) || 0), 0);
              const qty = parseFloat(params.data.quantity) || 0;
              return totalQty > 0 ? Number(((qty / totalQty) * 100).toFixed(1)) : 0;
            },
          };
        }
        if (def.type === "float") {
          return { ...base, ...floatFormater, editable };
        }
        if (def.field === "biogenic_gwp") {
          return {
            ...base,
            editable,
            valueFormatter: (p) => (p.value === undefined || p.value === null || p.value === "" ? "0" : p.value),
          };
        }
        return { ...base, editable };
      }),
    [itemDef, items, editable, renameLabel, t]
  );

  const addRow = () => {
    const defaultItem = itemDef.reduce((acc, def) => {
      acc[def.field] = def.default;
      return acc;
    }, {});
    onChange([...items, defaultItem]);
  };

  const removeSelected = () => {
    onChange(items.filter((_, i) => !selectedKeys.includes(i)));
    setSelectedKeys([]);
  };

  const onCellValueChanged = (params) => {
    const { colDef, newValue, data } = params;
    const idx = data.__key;
    onChange(items.map((item, i) => (i === idx ? { ...item, [colDef.field]: newValue } : item)));
  };

  const onSelectionChanged = (event) => {
    setSelectedKeys(event.api.getSelectedRows().map((r) => r.__key));
  };

  return (
    <Box sx={{ width: 1, mx: -3 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 2, mb: 0.5, pl: "20px", pr: 3 }}>
        <Typography sx={{ fontSize: "13px", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em", color: tok.text.muted, fontFamily: tok.font.family }}>
          {t(renameLabel(label))}
        </Typography>
        {editable && (
          <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
            <AddButton onClick={addRow} />
            <RemoveButton onClick={removeSelected} disabled={selectedKeys.length < 1} />
          </Box>
        )}
      </Box>
      <Box
        sx={{
          // ag-header-cell has a global 16px paddingLeft (!important), but
          // leftAlignColDef's cellStyle sets cell text paddingLeft to 20px —
          // that 4px gap made header labels sit left of their column's values.
          "& .ag-header-cell.ag-left-aligned-header": { paddingLeft: "20px !important" },
        }}
      >
        <AgGrid
          rowData={rowData}
          columnDefs={columnDefs}
          getRowId={(params) => String(params.data.__key)}
          onCellValueChanged={onCellValueChanged}
          onSelectionChanged={onSelectionChanged}
          pagination={true}
          domLayout="autoHeight"
        />
      </Box>
    </Box>
  );
};
export default MaterialCompositionGrid;
